import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/design-system/dropdown-menu';
import { IconButton } from '@/design-system/icon-button';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';
import {
  AgingTier,
  Invoice,
  InvoiceListParams,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  InvoiceStatus,
  InvoiceSummary,
  invoicesAPI,
} from '@/services/api/invoices';
import {
  AgingBadge,
  InvoicePositionBadge,
  InvoiceStatusPill,
} from './components/InvoiceBadges';
import HandoverDownloadModal from './components/HandoverDownloadModal';
import InvoiceDetailPanel, {
  DetailAction,
} from './components/InvoiceDetailPanel';
import ScanModal from './components/ScanModal';
import ExceptionModal from './components/ExceptionModal';
import ReopenModal from './components/ReopenModal';
import SubmitForConfirmModal from './components/SubmitForConfirmModal';
import DisputeModal from './components/DisputeModal';
import ResubmitModal from './components/ResubmitModal';
import ResolveModal from './components/ResolveModal';
import ExportPopover from './components/ExportPopover';
import FilterPanel, {
  EMPTY_FILTERS,
  DatePreset,
  InvoiceFilterValue,
  countActiveFilters,
} from './components/FilterPanel';
import {
  useUrlParam,
  useUrlNumber,
  useUrlPatch,
  useUrlSearch,
} from '@/hooks/urlState';
import ImportModal from './components/ImportModal';
import PerformancePanel from './components/PerformancePanel';

type TabKey =
  | 'ALL'
  | 'PENDING_COLLECTION'
  | 'RECEIVED_AT_HUB'
  | 'RECEIVED_BY_RIDER'
  | 'WAITING_CONFIRMATION'
  | 'DISPUTED'
  | 'RESOLVED'
  | 'EXCEPTION';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'PENDING_COLLECTION', label: 'Pending' },
  { key: 'RECEIVED_BY_RIDER', label: 'Received by Rider' },
  { key: 'RECEIVED_AT_HUB', label: 'Received at Hub' },
  { key: 'WAITING_CONFIRMATION', label: 'Awaiting confirm' },
  { key: 'DISPUTED', label: 'Disputed' },
  { key: 'RESOLVED', label: 'Resolved' },
  { key: 'EXCEPTION', label: 'Exception' },
];

const DEFAULT_PAGE_SIZE = 20;

const TAB_KEYS = TABS.map((t) => t.key) as readonly TabKey[];
// Switching a status tab clears the search term + page, but KEEPS the
// filter-popover filters (valid in every tab). Stable identity at module scope.
const TAB_RESET_PARAMS = ['q'] as const;
const DATE_PRESET_KEYS: readonly DatePreset[] = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
];
const AGING_TIERS: readonly AgingTier[] = ['green', 'amber', 'orange', 'red'];
const VIEWS = ['board', 'performance'] as const;
type InvoiceView = (typeof VIEWS)[number];

// URL param keys for the applied filter object (draft stays in local state).
const FILTER_PARAMS = {
  agingTier: 'aging',
  datePreset: 'preset',
  issuedFrom: 'from',
  issuedTo: 'to',
  clientId: 'client',
  outletId: 'outlet',
  hubId: 'hubId',
} as const;

const getSummaryCount = (
  summary: InvoiceSummary | null,
  tab: TabKey
): number | null => {
  if (!summary) return null;
  switch (tab) {
    case 'ALL':
      return summary.totalToday;
    case 'PENDING_COLLECTION':
      return summary.pending;
    case 'RECEIVED_AT_HUB':
      return summary.received;
    case 'RECEIVED_BY_RIDER':
      return summary.receivedByRider;
    case 'EXCEPTION':
      return summary.exception;
    case 'WAITING_CONFIRMATION':
    case 'DISPUTED':
    case 'RESOLVED':
      return null;
  }
};

const InvoicesPage: React.FC = () => {
  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [view, setView] = useUrlParam<InvoiceView>('view', 'board', {
    allowed: VIEWS,
    history: 'push',
  });
  // Board filter set when jumping from the performance drawer's
  // "Lihat status di board" — shows a removable chip above the table.
  const [driverFilter, setDriverFilter] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Tab / search / filters / pagination are URL-driven. Tab uses push history;
  // filter/tab/search changes drop `page` in the same URL write.
  const [tab, setTab] = useUrlParam<TabKey>('tab', 'ALL', {
    allowed: TAB_KEYS,
    resetPage: true,
    resetParams: TAB_RESET_PARAMS,
    history: 'push',
  });
  // Search is URL-backed; the box resyncs when the URL changes externally
  // (tab switch drops `q`, Back/forward, the performance→board jump).
  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { resetPage: true, delay: 500 });

  // Applied filters derived from the URL; draft stays local for the popover.
  const { searchParams, patch: patchUrl } = useUrlPatch();
  const filters: InvoiceFilterValue = useMemo(() => {
    const preset = searchParams.get(FILTER_PARAMS.datePreset);
    return {
      agingTier: (searchParams.get(FILTER_PARAMS.agingTier) ?? '')
        .split(',')
        .filter((t): t is AgingTier => AGING_TIERS.includes(t as AgingTier)),
      datePreset: DATE_PRESET_KEYS.includes(preset as DatePreset)
        ? (preset as DatePreset)
        : '',
      issuedFrom: searchParams.get(FILTER_PARAMS.issuedFrom) ?? '',
      issuedTo: searchParams.get(FILTER_PARAMS.issuedTo) ?? '',
      clientId: searchParams.get(FILTER_PARAMS.clientId) ?? '',
      outletId: searchParams.get(FILTER_PARAMS.outletId) ?? '',
      hubId: searchParams.get(FILTER_PARAMS.hubId) ?? '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const [filterDraft, setFilterDraft] =
    useState<InvoiceFilterValue>(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  const [items, setItems] = useState<Invoice[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [pageSize, setPageSize] = useUrlNumber('pageSize', DEFAULT_PAGE_SIZE, {
    min: 1,
    resetPage: true,
  });
  const [total, setTotal] = useState(0);

  const [scanOpen, setScanOpen] = useState(false);
  const [scanPreset, setScanPreset] = useState<Invoice | null>(null);
  const [exceptionTarget, setExceptionTarget] = useState<Invoice | null>(null);
  const [reopenTarget, setReopenTarget] = useState<Invoice | null>(null);
  const [submitForConfirmTarget, setSubmitForConfirmTarget] =
    useState<Invoice | null>(null);
  const [disputeTarget, setDisputeTarget] = useState<Invoice | null>(null);
  const [resubmitTarget, setResubmitTarget] = useState<Invoice | null>(null);
  const [resolveTarget, setResolveTarget] = useState<Invoice | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [handoverInvoices, setHandoverInvoices] = useState<Invoice[]>([]);
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await invoicesAPI.summary();
      setSummary(data);
    } catch (err) {
      toast.error('Could not load summary', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setTableLoading(true);
    const params: InvoiceListParams = {
      status: tab,
      page,
      pageSize,
      sort:
        tab === 'PENDING_COLLECTION' ? 'days_since:desc' : 'issue_date:desc',
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.agingTier.length > 0)
      params.agingTier = filters.agingTier.join(',');
    if (filters.clientId) params.clientId = filters.clientId;
    if (filters.outletId) params.outletId = filters.outletId;
    if (filters.hubId) params.hubId = filters.hubId;
    if (driverFilter) params.driverId = driverFilter.id;
    if (filters.datePreset) params.datePreset = filters.datePreset;
    if (filters.issuedFrom) params.issuedFrom = filters.issuedFrom;
    if (filters.issuedTo) params.issuedTo = filters.issuedTo;

    try {
      const data = await invoicesAPI.list(params);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setItems([]);
      setTotal(0);
      toast.error('Could not load invoices', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setTableLoading(false);
    }
  }, [tab, page, pageSize, debouncedSearch, filters, driverFilter]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (inEditable) return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        setScanPreset(null);
        setScanOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleRefresh = () => {
    fetchSummary();
    fetchList();
  };

  const handleInvoiceMutated = (updated: Invoice) => {
    setItems((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item))
    );
    fetchSummary();
  };

  const handleInvoicesReceived = (updated: Invoice[]) => {
    if (updated.length === 0) return;
    setItems((prev) =>
      prev.map((item) => updated.find((u) => u.id === item.id) ?? item)
    );
    fetchSummary();
    fetchList();
  };

  const activeFilterCount = useMemo(
    () => countActiveFilters(filters),
    [filters]
  );

  const handleFilterOpenChange = (open: boolean) => {
    if (open) setFilterDraft(filters);
    setFilterOpen(open);
  };

  // Write every filter field + drop `page` in ONE URL patch. `extra` lets a
  // caller fold in another param (e.g. a tab jump) so it stays a single write.
  const writeFilters = (
    v: InvoiceFilterValue,
    extra?: (params: URLSearchParams) => void
  ) => {
    patchUrl((params) => {
      const setOrDelete = (key: string, value: string) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      setOrDelete(FILTER_PARAMS.agingTier, v.agingTier.join(','));
      setOrDelete(FILTER_PARAMS.datePreset, v.datePreset);
      setOrDelete(FILTER_PARAMS.issuedFrom, v.issuedFrom);
      setOrDelete(FILTER_PARAMS.issuedTo, v.issuedTo);
      setOrDelete(FILTER_PARAMS.clientId, v.clientId);
      setOrDelete(FILTER_PARAMS.outletId, v.outletId);
      setOrDelete(FILTER_PARAMS.hubId, v.hubId);
      extra?.(params);
      params.delete('page');
    });
  };

  const applyFilter = () => {
    writeFilters(filterDraft);
    setFilterOpen(false);
  };

  const resetFilter = () => {
    setFilterDraft(EMPTY_FILTERS);
    writeFilters(EMPTY_FILTERS);
    setFilterOpen(false);
  };

  // ── Column registers (unified per CLAUDE.md ›› design critique pass) ──
  // Codes        : font-mono text-[12px] font-semibold text-ink tabular-nums
  // Dates        : font-sans text-[13px] text-text-sub-600 tabular-nums
  // Names        : font-sans text-[13px] text-text-strong-950 tracking-tight
  // Meta sub-line: font-sans text-[11px] text-text-sub-600
  const codeCls =
    'font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums';
  const dateCls = 'text-[13px] text-text-sub-600 tabular-nums';
  const nameCls = 'text-[13px] text-text-strong-950 tracking-tight';
  const metaCls = 'text-[11px] text-text-sub-600';

  const columns: TableDataColumn<Invoice>[] = [
    {
      key: 'awb',
      title: 'Delivery ID',
      render: (_v, record) => (
        // Single line. Batch id moved to the detail panel — keeping it here
        // made every row two lines tall and visually ragged because the
        // sibling columns were single-line.
        <span
          className={`${codeCls} cursor-pointer hover:text-accent hover:underline text-accent`}
          onClick={() => {
            window.open(`/delivery/${record.airwaybill}`, '_blank');
          }}
        >
          {record.airwaybill}
        </span>
      ),
    },
    {
      key: 'bookingId',
      title: 'Booking ID',
      render: (_v, record) => (
        <span className={codeCls}>{record.bookingId ?? '—'}</span>
      ),
    },
    {
      key: 'invoiceNumber',
      title: 'Invoice #',
      render: (_v, record) => (
        <span className={codeCls}>{record.invoiceNumber}</span>
      ),
    },
    {
      key: 'hub',
      title: 'Hub',
      render: (_v, record) => (
        <span className={nameCls}>{record.hub?.name ?? '—'}</span>
      ),
    },
    {
      key: 'client',
      title: 'Client / outlet',
      render: (_v, record) => (
        <div>
          <p className={nameCls}>{record.client.name}</p>
          <p className={metaCls}>{record.outlet?.name ?? '—'}</p>
        </div>
      ),
    },
    {
      key: 'rider',
      title: 'Rider',
      render: (_v, record) => {
        const riderName = record.rider?.name ?? '';
        const initial = riderName ? riderName.charAt(0).toUpperCase() : '·';
        return (
          <div className="flex items-center gap-2">
            {/* Neutral chip — primary purple is reserved for live/brand
                moments, not random initials. */}
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-bg-weak-50 text-[11px] font-semibold text-text-sub-600">
              {initial}
            </div>
            <span className={nameCls}>{riderName || '—'}</span>
          </div>
        );
      },
    },
    {
      key: 'issueDate',
      title: 'Issue date',
      // Date is data, not a code — sans + tabular, not mono.
      render: (_v, record) => (
        <span className={dateCls}>{record.issueDate}</span>
      ),
    },
    {
      key: 'position',
      title: 'Posisi Invoice',
      render: (_v, record) => <InvoicePositionBadge status={record.status} />,
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, record) => (
        <div className="space-y-1">
          <InvoiceStatusPill status={record.status} />
          {record.status === 'EXCEPTION' && record.notes && (
            <p className="max-w-[240px] truncate text-[11px] text-error-base">
              {record.notes}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'aging',
      title: 'Aging',
      render: (_v, record) => (
        <AgingBadge tier={record.agingTier} daysSince={record.daysSince} />
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, record) => {
        // Single hamburger per row. All status-aware actions live inside
        // the menu — the row stays calm, every row has identical visual
        // weight, and touch users get the same affordance as desktop.
        const actions: {
          key: string;
          label: string;
          icon: string;
          onClick: () => void;
          separateAfter?: boolean;
        }[] = [];

        if (
          record.status === 'PENDING_COLLECTION' ||
          record.status === 'RECEIVED_BY_RIDER'
        ) {
          actions.push({
            key: 'mark-received',
            label: 'Mark received',
            icon: 'ri-checkbox-circle-line text-accent',
            onClick: () => {
              setScanPreset(record);
              setScanOpen(true);
            },
          });
          if (record.status === 'RECEIVED_BY_RIDER') {
            actions.push({
              key: 'submit-for-confirm',
              label: 'Submit for confirm',
              icon: 'ri-send-plane-line text-sky-600',
              onClick: () => setSubmitForConfirmTarget(record),
            });
          }
          actions.push({
            key: 'flag-exception',
            label: 'Flag exception',
            icon: 'ri-alert-line text-rose-600',
            onClick: () => setExceptionTarget(record),
            separateAfter: false,
          });
        } else if (record.status === 'RECEIVED_AT_HUB') {
          actions.push({
            key: 'submit-for-confirm',
            label: 'Submit for confirm',
            icon: 'ri-send-plane-line text-sky-600',
            onClick: () => setSubmitForConfirmTarget(record),
          });
          actions.push({
            key: 'flag-exception',
            label: 'Flag exception',
            icon: 'ri-alert-line text-rose-600',
            onClick: () => setExceptionTarget(record),
          });
        } else if (record.status === 'WAITING_CONFIRMATION') {
          actions.push({
            key: 'resolve',
            label: 'Mark resolved',
            icon: 'ri-checkbox-circle-line text-emerald-600',
            onClick: () => setResolveTarget(record),
          });
          actions.push({
            key: 'dispute',
            label: 'Mark disputed',
            icon: 'ri-error-warning-line text-rose-600',
            onClick: () => setDisputeTarget(record),
          });
        } else if (record.status === 'DISPUTED') {
          actions.push({
            key: 'resubmit',
            label: 'Resubmit for confirm',
            icon: 'ri-refresh-line text-sky-600',
            onClick: () => setResubmitTarget(record),
          });
        } else if (record.status === 'EXCEPTION') {
          actions.push({
            key: 'reopen',
            label: 'Reopen and mark received',
            icon: 'ri-arrow-go-back-line text-amber-600',
            onClick: () => setReopenTarget(record),
          });
        }

        // Always-available actions (separator before the destructive set).
        if (actions.length > 0) {
          // Mark the last status-action as `separateAfter` so the divider
          // sits between it and the "View details" entry below.
          actions[actions.length - 1] = {
            ...actions[actions.length - 1],
            separateAfter: true,
          };
        }
        actions.push({
          key: 'view-details',
          label: 'View details',
          icon: 'ri-eye-line text-text-sub-600',
          onClick: () => setDetailInvoiceId(record.id),
        });

        // Terminal statuses (e.g. RESOLVED) still get a kebab so the row
        // affordance is consistent across the table — they just only see
        // "View details".
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                tone="neutral"
                style="ghost"
                size="sm"
                aria-label={`Actions for ${record.invoiceNumber}`}
              >
                <i className="ri-more-2-fill text-[18px]" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {actions.map((a) => (
                <React.Fragment key={a.key}>
                  <DropdownMenuItem onSelect={a.onClick}>
                    <i className={a.icon} />
                    {a.label}
                  </DropdownMenuItem>
                  {a.separateAfter && <DropdownMenuSeparator />}
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            Invoice status board
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
            Scan returned surat jalan, track pending returns, and flag
            exceptions before they reach payment reconciliation.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button tone="neutral" style="stroke" onClick={handleRefresh}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>
          <ExportPopover open={exportOpen} onOpenChange={setExportOpen} />
          <Button
            tone="neutral"
            style="stroke"
            onClick={() => setImportOpen(true)}
          >
            <i className="ri-upload-2-line mr-1.5 text-[14px]" />
            Import
          </Button>
          <Button
            onClick={() => {
              setScanPreset(null);
              setScanOpen(true);
            }}
          >
            <i className="ri-qr-scan-2-line mr-1.5 text-[16px]" />
            Scan invoices
            <span className="ml-2 rounded-md border border-white/30 bg-white/15 px-1.5 py-0.5 font-mono text-[10px] font-bold tabular">
              S
            </span>
          </Button>
        </div>
      </div>

      <div className="flex items-center">
        <SegmentedControl
          value={view}
          // Radix single-toggle emits "" when the active item is re-clicked,
          // which would deselect both segments. Ignore the empty value so the
          // current view stays selected.
          onValueChange={(v: string) => {
            if (v) setView(v as 'board' | 'performance');
          }}
        >
          <SegmentedItem value="board">Status board</SegmentedItem>
          <SegmentedItem value="performance">
            Performance · Hub &amp; Rider
          </SegmentedItem>
        </SegmentedControl>
      </div>

      {view === 'performance' ? (
        <PerformancePanel
          onOpenBoardForRider={(id, name) => {
            setDriverFilter({ id, name });
            // tab=ALL and view=board are defaults → drop them; reset page.
            // One push write so Back returns to the performance view.
            patchUrl((p) => {
              p.delete('tab');
              p.delete('view');
              p.delete('page');
            }, 'push');
          }}
        />
      ) : (
        <>
          <SummaryBar
            summary={summary}
            loading={summaryLoading}
            onJumpRed={() =>
              writeFilters({ ...EMPTY_FILTERS, agingTier: ['red'] }, (p) =>
                p.set('tab', 'PENDING_COLLECTION')
              )
            }
          />

          <Card padding="none">
            <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
              <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
                <TabsList variant="line" className="w-auto border-0">
                  {TABS.map((t) => {
                    const count = getSummaryCount(summary, t.key);
                    return (
                      <TabsTrigger key={t.key} value={t.key}>
                        {t.label}
                        {count !== null && <TabsCount>{count}</TabsCount>}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>

              <div className="flex flex-1 items-center justify-end gap-2 lg:py-2">
                <SearchField
                  ref={searchInputRef}
                  value={search}
                  onChange={setSearch}
                  placeholder="Search invoice, AWB, rider, outlet…"
                  size="sm"
                  className="max-w-xs flex-1"
                />
                <FilterPanel
                  open={filterOpen}
                  onOpenChange={handleFilterOpenChange}
                  activeFilterCount={activeFilterCount}
                  value={filterDraft}
                  onChange={setFilterDraft}
                  onApply={applyFilter}
                  onReset={resetFilter}
                />
              </div>
            </div>

            {driverFilter && (
              <div className="flex items-center gap-2 border-b border-stroke-soft-200 px-4 py-2">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-stroke-soft-200 bg-bg-weak-50 px-2 py-1 text-[12px] font-medium text-text-strong-950">
                  Rider: <b>{driverFilter.name}</b>
                  <button
                    type="button"
                    onClick={() => {
                      setDriverFilter(null);
                      setPage(1);
                    }}
                    aria-label="Clear rider filter"
                    className="ml-1 hover:opacity-70"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              </div>
            )}

            <div className="p-4">
              <TableData<Invoice>
                data={items}
                columns={columns}
                loading={tableLoading}
                size="sm"
                // onRowClick={(record) => setDetailInvoiceId(record.id)}
              />

              {total > 0 && (
                <Paginator
                  currentPage={page}
                  totalItems={total}
                  itemsPerPage={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              )}
            </div>
          </Card>
        </>
      )}

      <ScanModal
        isOpen={scanOpen}
        presetInvoice={scanPreset}
        onClose={() => {
          setScanOpen(false);
          setScanPreset(null);
        }}
        onReceived={handleInvoicesReceived}
        onSubmitted={(submitted) => setHandoverInvoices(submitted)}
      />

      <HandoverDownloadModal
        isOpen={handoverInvoices.length > 0}
        invoices={handoverInvoices}
        onClose={() => setHandoverInvoices([])}
      />

      <InvoiceDetailPanel
        invoiceId={detailInvoiceId}
        onClose={() => setDetailInvoiceId(null)}
        onAction={(action: DetailAction, invoice) => {
          // Reuse the existing modal targets so the transition logic stays
          // in one place. Closing the panel here keeps the detail view from
          // sitting on top of the action modal.
          setDetailInvoiceId(null);
          if (action === 'receive') {
            setScanPreset(invoice);
            setScanOpen(true);
          } else if (action === 'submit') {
            setSubmitForConfirmTarget(invoice);
          } else if (action === 'dispute') {
            setDisputeTarget(invoice);
          } else if (action === 'resolve') {
            setResolveTarget(invoice);
          } else if (action === 'resubmit') {
            setResubmitTarget(invoice);
          } else if (action === 'reopen') {
            setReopenTarget(invoice);
          } else if (action === 'exception') {
            setExceptionTarget(invoice);
          }
        }}
      />

      <ExceptionModal
        isOpen={exceptionTarget !== null}
        invoice={exceptionTarget}
        onClose={() => setExceptionTarget(null)}
        onUpdated={handleInvoiceMutated}
      />

      <ReopenModal
        isOpen={reopenTarget !== null}
        invoice={reopenTarget}
        onClose={() => setReopenTarget(null)}
        onUpdated={handleInvoiceMutated}
      />

      <SubmitForConfirmModal
        isOpen={submitForConfirmTarget !== null}
        invoice={submitForConfirmTarget}
        onClose={() => setSubmitForConfirmTarget(null)}
        onUpdated={handleInvoiceMutated}
      />

      <DisputeModal
        isOpen={disputeTarget !== null}
        invoice={disputeTarget}
        onClose={() => setDisputeTarget(null)}
        onUpdated={handleInvoiceMutated}
      />

      <ResubmitModal
        isOpen={resubmitTarget !== null}
        invoice={resubmitTarget}
        onClose={() => setResubmitTarget(null)}
        onUpdated={handleInvoiceMutated}
      />

      <ResolveModal
        isOpen={resolveTarget !== null}
        invoice={resolveTarget}
        onClose={() => setResolveTarget(null)}
        onUpdated={handleInvoiceMutated}
      />

      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          fetchSummary();
          fetchList();
        }}
      />
    </div>
  );
};

interface SummaryBarProps {
  summary: InvoiceSummary | null;
  loading: boolean;
  onJumpRed: () => void;
}

const SummaryBar: React.FC<SummaryBarProps> = ({
  summary,
  loading,
  onJumpRed,
}) => {
  const cards: {
    label: string;
    value: number | null;
    dot: string;
    sub?: string;
    alert?: boolean;
    onClick?: () => void;
  }[] = [
    {
      label: 'Total today',
      value: summary?.totalToday ?? null,
      dot: 'bg-text-soft-400',
    },
    {
      label: 'Received by Rider',
      value: summary?.receivedByRider ?? null,
      dot: 'bg-(--state-success-base)',
      sub: summary
        ? `${Math.round((summary.receivedByRider / summary.pending) * 100)}% of today`
        : undefined,
    },
    {
      label: 'Received at Hub',
      value: summary?.received ?? null,
      dot: 'bg-(--state-success-base)',
      sub: summary
        ? `${Math.round((summary.received / summary.pending) * 100)}% of today`
        : undefined,
    },
    {
      label: 'Pending',
      value: summary?.pending ?? null,
      dot: 'bg-(--state-warning-base)',
    },
    {
      label: 'Exception',
      value: summary?.exception ?? null,
      dot: 'bg-(--state-warning-base)',
    },
    {
      label: 'Awaiting Confirmation',
      value: summary?.awaitingConfirmation ?? 0,
      dot: 'bg-(--state-information-base)',
    },
    {
      label: 'Disputed',
      value: summary?.disputed ?? 0,
      dot: 'bg-(--state-error-dark)',
    },
    {
      label: 'Red alerts',
      value: summary?.redAlerts ?? null,
      dot: 'bg-(--state-error-base)',
      sub: '7+ days overdue',
      alert: (summary?.redAlerts ?? 0) > 0,
      onClick: (summary?.redAlerts ?? 0) > 0 ? onJumpRed : undefined,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
      {cards.map((card) => (
        <button
          key={card.label}
          type="button"
          disabled={!card.onClick}
          onClick={card.onClick}
          className={`rounded-lg border bg-bg-white-0 p-4 text-left transition-colors ${
            card.alert
              ? 'border-error-light bg-error-lighter/50'
              : 'border-stroke-soft-200'
          } ${card.onClick ? 'hover:border-accent cursor-pointer' : 'cursor-default'}`}
        >
          <div className="mb-3 flex items-center gap-2 label-eyebrow">
            <span className={`h-2 w-2 rounded-full ${card.dot}`} />
            {card.label}
          </div>
          <div
            className={`text-[32px] font-extrabold leading-none tracking-tight tabular-nums ${
              card.alert ? 'text-error-base' : 'text-text-strong-950'
            }`}
          >
            {loading || card.value === null ? (
              <span className="inline-block h-8 w-12 animate-pulse rounded-md bg-bg-soft-200" />
            ) : (
              card.value
            )}
          </div>
          {card.sub && (
            <p className="mt-3 text-[11px] text-text-sub-600 font-mono tabular border-t border-stroke-soft-200 pt-2">
              {card.sub}
            </p>
          )}
        </button>
      ))}
    </div>
  );
};

export default InvoicesPage;
